import _ from 'lodash';
import {
  FormControl,
  FormHelperText,
  Typography,
  GridLegacy,
  Stack,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form';
import { ReactNode } from 'react';
import ClickableMainCard from 'ui-component/cards/ClickableMainCard';
import { actionButtonSpacing, gridSpacing } from 'constants/themeConstants';
import { Icon } from '@tabler/icons-react';
import { SxProps } from '@mui/system';
import { Theme, useTheme } from '@mui/material/styles';
import Chip, { ChipColor } from 'ui-component/extended/Chip';

interface HookFormToggleCardButtonProps {
  errors?: FieldErrors;
  control: Control;
  name: string;
  label: string | ReactNode;
  options: {
    value: unknown;
    label: string;
    description: string;
    icon?: Icon;
    chipText?: string;
  }[];
  rules?: RegisterOptions;
  defaultValue?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
  valueAsInt?: boolean;
  sx?: SxProps<Theme>;
  [key: string]: unknown;
  alert?: ReactNode;
}

export const HookFormToggleCardButton = ({
  errors,
  control,
  name,
  label,
  options,
  defaultValue,
  disabled,
  multiple = false,
  valueAsInt = false,
  sx,
  alert,
  ...rest
}: HookFormToggleCardButtonProps) => {
  const theme = useTheme();
  return (
    <FormControl fullWidth sx={{ mb: 2, ...sx }}>
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, width: '80%' }}>
        {label}
      </Typography>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rest.rules}
        render={({ field: { onChange, value } }) => (
          <GridLegacy container spacing={1} alignItems="stretch">
            {options.map((option) => {
              const CardIcon = option.icon;
              return (
                <GridLegacy
                  key={option.label}
                  item
                  xs={Math.ceil(12 / options.length)}
                  sx={{ width: '100%', display: 'flex' }}
                >
                  <ClickableMainCard
                    sx={{
                      flexGrow: 1,
                      backgroundColor: _.isEqual(value, option.value)
                        ? theme.palette.success.light
                        : undefined,
                    }}
                    headerSX={{ p: actionButtonSpacing }}
                    contentSX={{ p: actionButtonSpacing }}
                    title={
                      <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                      >
                        {option.icon && CardIcon && <CardIcon />}
                        <Typography variant="h4">{option.label}</Typography>
                        {option.chipText && (
                          <Chip
                            label={option.chipText}
                            size="small"
                            chipcolor={ChipColor.success}
                          />
                        )}
                      </Stack>
                    }
                    boxShadow={!disabled}
                    onClick={() => onChange(option.value)}
                    highlight={_.isEqual(value, option.value)}
                    disabled={disabled}
                  >
                    <Typography>{option.description}</Typography>
                  </ClickableMainCard>
                </GridLegacy>
              );
            })}
            {errors?.[name]?.message && (
              <FormHelperText error id={`${name}Error`} sx={{ ml: 0 }}>
                {String(
                  (errors?.[name] as { message?: string })?.message || ''
                )}
              </FormHelperText>
            )}
            {!!alert && (
              <GridLegacy item xs={12} sx={{ mt: gridSpacing }}>
                {alert}
              </GridLegacy>
            )}
          </GridLegacy>
        )}
      />
    </FormControl>
  );
};
